import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import type { GoogleStatus, ImportItem, ImportLimits } from '../types';
import DeviceImport from '../components/import/DeviceImport';
import DriveImport from '../components/import/DriveImport';
import ImportActivity, { SETTLED } from '../components/import/ImportActivity';

/** Show imports from the last few hours; older history isn't "recent". */
const RECENT_MS = 12 * 60 * 60 * 1000;
const POLL_MS = 1200;

/** Outcomes the server appends after Google's consent screen (?google=...). */
const OAUTH_OUTCOMES: Record<string, { tone: 'ok' | 'warn' | 'error'; text: string }> = {
  connected: { tone: 'ok', text: 'Google Drive is connected. Choose the photos and videos to import.' },
  denied: { tone: 'warn', text: 'Google Drive wasn’t connected because you cancelled on Google’s page. Nothing was shared.' },
  insufficient_scope: {
    tone: 'warn',
    text: 'Google Drive wasn’t connected because access to the files you choose wasn’t allowed. Connect again and keep that permission ticked.',
  },
  invalid_state: {
    tone: 'error',
    text: 'That sign-in attempt expired or didn’t start here, so it was ignored. Try connecting again.',
  },
  not_configured: { tone: 'error', text: 'Google Drive import isn’t set up on this server.' },
  error: { tone: 'error', text: 'Google Drive couldn’t be connected. Try again in a moment.' },
};

export default function ImportPage() {
  const [params, setParams] = useSearchParams();
  const [limits, setLimits] = useState<ImportLimits | null>(null);
  const [google, setGoogle] = useState<GoogleStatus | null>(null);
  const [items, setItems] = useState<ImportItem[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [outcome] = useState(() => params.get('google'));
  const timer = useRef<number | null>(null);

  // Drop ?google= from the URL so a refresh doesn't repeat the message.
  useEffect(() => {
    if (params.has('google')) setParams({}, { replace: true });
  }, [params, setParams]);

  const refreshGoogle = useCallback(() => {
    api
      .googleStatus()
      .then(setGoogle)
      .catch(() => setGoogle({ configured: false, missing: [], requestedScopes: [], connected: false }));
  }, []);

  const refreshItems = useCallback(async () => {
    try {
      const res = await api.imports({ limit: 100 });
      const cutoff = Date.now() - RECENT_MS;
      const recent = res.items.filter((i) => new Date(i.createdAt).getTime() >= cutoff);
      setItems(recent);
      setLoadError(null);
      return recent;
    } catch (err) {
      setLoadError((err as Error).message);
      return [];
    }
  }, []);

  // Poll while anything is still moving through the pipeline.
  const poll = useCallback(async () => {
    if (timer.current) window.clearTimeout(timer.current);
    const recent = await refreshItems();
    if (recent.some((i) => !SETTLED.includes(i.status))) {
      timer.current = window.setTimeout(poll, POLL_MS);
    }
  }, [refreshItems]);

  useEffect(() => {
    api.importLimits().then(setLimits).catch(() => setLimits(null));
    refreshGoogle();
    void poll();
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [poll, refreshGoogle]);

  const banner = outcome ? OAUTH_OUTCOMES[outcome] ?? OAUTH_OUTCOMES.error : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-10">
      <header className="mb-8 max-w-2xl">
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-5xl">Import your memories</h1>
        <p className="mt-3 text-white/55">
          Add photos and videos from this device or from Google Drive. Memoryflix reads when and where each one
          was taken and files it into the right season and episode.
        </p>
      </header>

      {banner && (
        <div
          role={banner.tone === 'ok' ? 'status' : 'alert'}
          className={`mb-6 rounded-lg border p-3 text-sm ${
            banner.tone === 'ok'
              ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-100'
              : banner.tone === 'warn'
                ? 'border-amber-300/30 bg-amber-300/10 text-amber-100'
                : 'border-red-500/30 bg-red-500/10 text-red-100'
          }`}
        >
          {banner.text}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr]">
        <section className="rounded-2xl bg-ink-900 p-5 ring-1 ring-white/5 sm:p-6" aria-label="Upload from this device">
          <DeviceImport limits={limits} onUploaded={() => void poll()} />
        </section>
        <section className="rounded-2xl bg-ink-900 p-5 ring-1 ring-white/5 sm:p-6" aria-label="Import from Google Drive">
          <DriveImport status={google} onStatusChange={refreshGoogle} onImported={() => void poll()} />
        </section>
      </div>

      {loadError && (
        <p className="mt-8 text-sm text-red-200" role="alert">
          Couldn’t load import progress: {loadError}
        </p>
      )}

      <ImportActivity items={items} onChanged={() => void poll()} />
    </div>
  );
}
